import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import React__default, { useRef, useState, useEffect } from "react";
import classNames from "classnames";
import { ZIndex, AnalyticsId } from "./constants.js";
import { useInterval } from "./hooks/useInterval.js";
import { useIsVisible } from "./hooks/useIsVisible.js";
import { useLayoutEvent } from "./hooks/useLayoutEvent.js";
import { useSize } from "./hooks/useSize.js";
import { mergeRefs } from "./utils/refs.js";
import styles from "./components/PopOver/styles.module.scss";
const BUBBLE_WIDTH_PX = 373;
const WINDOW_MARGIN_PX = 12;
const BUBBLE_VERTICAL_OFFSET_PX = 16;
const ARROW_WIDTH_PX = 20;
const ARROW_VERTICAL_OFFSET_PX = 4;
const ARROW_HORIZONTAL_MARGIN_PX = 12;
const getVerticalPosition = (controllerSize, bubbleSize, variant) => {
  if (variant !== PopOverVariant.positionautomatic) {
    return variant;
  }
  if (controllerSize.top > bubbleSize.height + BUBBLE_VERTICAL_OFFSET_PX) {
    return PopOverVariant.positionabove;
  } else {
    return PopOverVariant.positionbelow;
  }
};
const getControllerLeftCenterPx = (controllerSize) => controllerSize.left + controllerSize.width / 2;
const getControllerRightCenterPx = (controllerSize) => document.documentElement.clientWidth - controllerSize.right + controllerSize.width / 2;
const getBubbleLeftPx = (controllerSize, bubbleSize) => {
  const controllerHorizontalCenterPx = getControllerLeftCenterPx(controllerSize);
  return controllerHorizontalCenterPx - bubbleSize.width / 2;
};
const getBubbleRightPx = (controllerSize, bubbleSize) => {
  const bubbleLeftPx = getBubbleLeftPx(controllerSize, bubbleSize);
  return bubbleLeftPx + bubbleSize.width;
};
const getBubbleLeftVisible = (controllerSize, bubbleSize) => {
  const bubbleLeftPx = getBubbleLeftPx(controllerSize, bubbleSize);
  return bubbleLeftPx > WINDOW_MARGIN_PX;
};
const getBubbleRightIsVisible = (controllerSize, bubbleSize) => {
  const bubbleRightPx = getBubbleRightPx(controllerSize, bubbleSize);
  return bubbleRightPx < document.documentElement.clientWidth - WINDOW_MARGIN_PX;
};
const getHorizontalPosition = (controllerSize, bubbleSize) => {
  if (!getBubbleRightIsVisible(controllerSize, bubbleSize)) {
    return "right";
  }
  if (!getBubbleLeftVisible(controllerSize, bubbleSize)) {
    return "left";
  }
  return "floating";
};
const getBubbleAbovePx = (controllerSize, bubbleSize) => controllerSize.top - BUBBLE_VERTICAL_OFFSET_PX - bubbleSize.height;
const getBubbleBelowPx = (controllerSize) => controllerSize.bottom + BUBBLE_VERTICAL_OFFSET_PX;
const getBubbleWidth = () => document.documentElement.clientWidth - WINDOW_MARGIN_PX * 2;
const getBubbleFitsInWindow = () => {
  return document.documentElement.clientWidth > BUBBLE_WIDTH_PX + WINDOW_MARGIN_PX * 2;
};
const getArrowTopxPx = (controllerSize) => controllerSize.top - BUBBLE_VERTICAL_OFFSET_PX - ARROW_VERTICAL_OFFSET_PX;
const getArrowLeftPx = (controllerSize) => getControllerLeftCenterPx(controllerSize) - ARROW_WIDTH_PX / 2;
const getArrowRightPx = (controllerSize) => getControllerRightCenterPx(controllerSize) - ARROW_WIDTH_PX / 2;
const getBubblePosition = (controllerSize, bubbleSize, variant) => {
  const horizontalPosition = getHorizontalPosition(controllerSize, bubbleSize);
  const verticalPosition = getVerticalPosition(controllerSize, bubbleSize, variant);
  if (horizontalPosition === "left") {
    if (verticalPosition === PopOverVariant.positionabove) {
      return "leftabove";
    }
    return "leftbelow";
  }
  if (horizontalPosition === "right") {
    if (verticalPosition === PopOverVariant.positionabove) {
      return "rightabove";
    }
    return "rightbelow";
  }
  if (verticalPosition === PopOverVariant.positionabove) {
    return "floatingabove";
  }
  return "floatingbelow";
};
const getBubbleStyle = (controllerSize, bubbleSize, variant) => {
  const bubblePosition = getBubblePosition(controllerSize, bubbleSize, variant);
  const bubbleWidth = !getBubbleFitsInWindow() ? getBubbleWidth() : void 0;
  if (bubblePosition === "leftabove") {
    return {
      left: WINDOW_MARGIN_PX,
      top: getBubbleAbovePx(controllerSize, bubbleSize),
      width: bubbleWidth
    };
  }
  if (bubblePosition === "leftbelow") {
    return { left: WINDOW_MARGIN_PX, top: getBubbleBelowPx(controllerSize), width: bubbleWidth };
  }
  if (bubblePosition === "rightabove") {
    return { right: WINDOW_MARGIN_PX, top: getBubbleAbovePx(controllerSize, bubbleSize), width: bubbleWidth };
  }
  if (bubblePosition === "rightbelow") {
    return { right: WINDOW_MARGIN_PX, top: getBubbleBelowPx(controllerSize), width: bubbleWidth };
  }
  if (bubblePosition === "floatingbelow") {
    return { left: getBubbleLeftPx(controllerSize, bubbleSize), top: getBubbleBelowPx(controllerSize), width: bubbleWidth };
  }
  return { left: getBubbleLeftPx(controllerSize, bubbleSize), top: getBubbleAbovePx(controllerSize, bubbleSize), width: bubbleWidth };
};
const getArrowStyle = (bubbleStyle, controllerSize, verticalPosition) => {
  const leftPx = getArrowLeftPx(controllerSize);
  const rightPx = getArrowRightPx(controllerSize);
  const minLeftPx = bubbleStyle.left + ARROW_HORIZONTAL_MARGIN_PX;
  const minRightPx = bubbleStyle.right + ARROW_HORIZONTAL_MARGIN_PX;
  if (bubbleStyle.right) {
    if (verticalPosition === PopOverVariant.positionabove) {
      return {
        right: rightPx > minRightPx ? rightPx : minRightPx,
        top: getArrowTopxPx(controllerSize)
      };
    }
    return {
      right: rightPx > minRightPx ? rightPx : minRightPx,
      top: controllerSize.bottom
    };
  }
  if (verticalPosition === PopOverVariant.positionabove) {
    return {
      left: leftPx > minLeftPx ? leftPx : minLeftPx,
      top: getArrowTopxPx(controllerSize)
    };
  }
  return {
    left: leftPx > minLeftPx ? leftPx : minLeftPx,
    top: controllerSize.bottom
  };
};
var PopOverVariant = /* @__PURE__ */ ((PopOverVariant2) => {
  PopOverVariant2["positionautomatic"] = "positionautomatic";
  PopOverVariant2["positionbelow"] = "positionbelow";
  PopOverVariant2["positionabove"] = "positionabove";
  return PopOverVariant2;
})(PopOverVariant || {});
const PopOver = React__default.forwardRef((props, ref) => {
  const {
    id,
    children,
    controllerRef,
    popOverRef,
    show = false,
    className = "",
    variant = "positionautomatic",
    role,
    testId,
    arrowClassName,
    zIndex = ZIndex.PopOver
  } = props;
  const bubbleRef = popOverRef || useRef(null);
  const arrowRef = useRef(null);
  const bubbleSize = useSize(bubbleRef);
  const [controllerSize, setControllerSize] = useState();
  const controllerisVisible = useIsVisible(bubbleRef, 0);
  const updateControllerSize = () => {
    var _a;
    setControllerSize((_a = controllerRef.current) == null ? void 0 : _a.getBoundingClientRect());
  };
  useInterval(updateControllerSize, 500);
  useLayoutEvent(updateControllerSize, ["scroll", "resize"], 10);
  useEffect(() => {
    updateControllerSize();
  }, []);
  const isTooltip = role === "tooltip";
  const popOverClasses = classNames(styles.popover, { [styles["popover--visible"]]: isTooltip ? show : controllerisVisible }, className);
  const verticalPosition = controllerSize && bubbleSize && getVerticalPosition(controllerSize, bubbleSize, variant);
  const arrowClasses = classNames(styles.popover__arrow, arrowClassName, {
    [styles["popover__arrow--over"]]: verticalPosition === "positionbelow",
    [styles["popover__arrow--under"]]: verticalPosition === "positionabove",
    [styles["popover__arrow--visible"]]: isTooltip ? show : controllerisVisible
  });
  const bubbleStyle = controllerSize && bubbleSize && getBubbleStyle(controllerSize, bubbleSize, variant);
  const arrowStyle = bubbleStyle && controllerSize && verticalPosition && getArrowStyle(bubbleStyle, controllerSize, verticalPosition);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        id,
        ref: mergeRefs([ref, bubbleRef]),
        className: popOverClasses,
        style: { ...bubbleStyle, zIndex },
        "data-testid": testId,
        "data-analyticsid": AnalyticsId.PopOver,
        role,
        children
      }
    ),
    /* @__PURE__ */ jsx("div", { ref: arrowRef, className: arrowClasses, style: { ...arrowStyle, zIndex } })
  ] });
});
PopOver.displayName = "PopOver";
export {
  PopOverVariant as P,
  PopOver as a
};
//# sourceMappingURL=PopOver.js.map
